import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandYoutube,
  IconArrowRight,
  IconBolt,
  IconBookmark,
  IconCalendar,
  IconCheck,
  IconGlobe,
  IconHeart,
  IconLink,
  IconMail,
  IconMapPin,
  IconMinus,
  IconPhone,
  IconPlus,
  IconQuote,
  IconShare,
  IconStar,
  IconUser,
  IconUsers,
  IconWorld,
} from "@tabler/icons-react";
import type { Icon as TablerIconType } from "@tabler/icons-react";

import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { useExpandable } from "./expandableContext";
import { safeHref } from "../lib/safe-href";
import { type BorderSide, classNames, extractUtilityClasses, getBorderClasses, toCssLength } from "./shared";

const ICON_SIZE_MAP: Record<string, number> = {
  small: 16,
  medium: 24,
  large: 32,
};

export const TABLER_ICON_MAP: Record<string, TablerIconType> = {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandYoutube,
  IconArrowRight,
  IconBolt,
  IconBookmark,
  IconCalendar,
  IconCheck,
  IconGlobe,
  IconHeart,
  IconLink,
  IconMail,
  IconMapPin,
  IconMinus,
  IconPhone,
  IconPlus,
  IconQuote,
  IconShare,
  IconStar,
  IconUser,
  IconUsers,
  IconWorld,
};

export function IconChild({
  isVisible = true,
  icon,
  iconName,
  iconColor,
  iconSize = "medium",
  size,
  color,
  iconAnimationMode,
  expandedIconName = "IconMinus",
  href,
  padding,
  backgroundColor,
  border = false,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
  borderRadius,
}: {
  isVisible?: boolean;
  icon?: string;
  iconName?: string;
  iconColor?: string;
  iconSize?: "small" | "medium" | "large";
  size?: number;
  color?: string;
  iconAnimationMode?: "rotate" | "swap" | "none";
  expandedIconName?: string;
  href?: string;
  padding?: string;
  backgroundColor?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
  borderRadius?: string;
}) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  const expandable = useExpandable();
  if (!isVisible) return null;
  const colorOverride = iconColor ?? color;
  const resolvedColor =
    typeof colorOverride === "string" && colorOverride.trim().length > 0
      ? colorOverride
      : currentColorPalette.primary;
  const resolvedSize = size ?? ICON_SIZE_MAP[iconSize] ?? 24;

  const isExpanded = expandable?.isExpanded ?? false;

  // Determine which icon to show (swap mode switches to expandedIconName when expanded)
  const baseIconName = iconName ?? icon;
  const resolvedIconName =
    iconAnimationMode === "swap" && isExpanded ? expandedIconName : baseIconName;

  // Determine rotation class (rotate mode rotates 45deg when expanded)
  const rotationClass =
    iconAnimationMode === "rotate" && isExpanded ? "rotate-45" : "";

  const TablerIcon = resolvedIconName
    ? TABLER_ICON_MAP[resolvedIconName]
    : undefined;

  if (!TablerIcon) {
    return (
      <span
        style={{
          display: "inline-flex",
          fontSize: `${resolvedSize}px`,
          color: resolvedColor,
        }}
      >
        {resolvedIconName ?? "•"}
      </span>
    );
  }

  const borderCls = getBorderClasses(border, borderWidth || "border", borderStyle || "border-solid", borderSides);
  const roundedCls = extractUtilityClasses(
    borderRadius ?? (border ? currentRounding : undefined),
    "rounded",
  );

  const iconElement = (
    <div
      className={classNames(
        "flex items-center justify-center shrink-0 transition-transform duration-300",
        rotationClass,
        padding,
        borderCls,
        roundedCls,
      )}
      style={{
        backgroundColor: backgroundColor || undefined,
        borderColor: border ? (borderColor || currentColorPalette.primary) : undefined,
        borderRadius: roundedCls ? undefined : toCssLength(borderRadius ?? (border ? currentRounding : undefined)),
      }}
    >
      <TablerIcon size={resolvedSize} color={resolvedColor} />
    </div>
  );

  if (href) {
    return (
      <a
        href={safeHref(href)}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "inline-flex" }}
      >
        {iconElement}
      </a>
    );
  }

  return iconElement;
}
